import { compose } from "redux";
import { connect } from "react-redux";
import React, { PureComponent } from "react";
import { formValueSelector } from "redux-form/immutable";
import { Grid, withStyles, Link, Divider } from "@material-ui/core";

import { SortableList, CompetencyStatusChip } from "altus-ui-components";

import {
  REQUIREMENT_TYPES,
  CREATE_CARD_MOVE_FORM,
  MISSING_COMPETENCE_MODAL_ID,
} from "features/planner/planner.constants";

import {
  getSummarizedCardCompetencyStatusFromState,
  getCardMissingCompetencyStatusItemsFromState,
} from "features/planner/planner.selectors";

import {
  criticalityToString,
  requirementTypeToString,
} from "features/planner/planner.mappers";

import cardService from "services/card.service";
import { EMPTY_LIST, ICONS } from "app/app.constants";
import SimpleFormModal from "app/components/SimpleFormModal";
import { allocateEmployeeOnSubmit } from "features/planner/planner.actions";
import CompetencyStatusIcon from "features/planner/components/CompetencyStatusIcon";
import CreateCardMoveTopContent from "features/planner/components/CreateCardMove/CreateCardMoveTopContent";

class SpecificationGapReportModal extends PureComponent {
  redirectToSpecificationGapReport = event => {
    event.preventDefault();
    event.stopPropagation();

    const { cardId, groupId } = this.props;

    return cardService.redirectToSpecificationGapReportForGroupAndCardId(
      cardId,
      groupId,
    );
  };

  createColumns = cardAllocated => [
    {
      xs: true,
      title: "Name",
      getSortProperty: item => item.get("name"),
    },
    {
      xs: true,
      title: "Type",
      getSortProperty: item => requirementTypeToString[item.get("type")],
    },
    {
      xs: true,
      title: "Specification",
      filter: {
        defaultText: "All",
      },
      getSortProperty: item => item.get("specificationName"),
    },
    {
      xs: true,
      title: "Criticality",
      filter: !cardAllocated && {
        defaultText: "All",
      },
      getSortProperty: item => criticalityToString[item.get("criticality")],
    },
    {
      xs: true,
      title: "Status",
      component: Grid,
      container: true,
      justify: "flex-end",
      getSortProperty: item => item.get("competencyStatus"),
      getValue: item => (
        <CompetencyStatusChip
          status={item.get("competencyStatus")}
          renderCompetencyStatusIcon={this.renderCompetencyStatusIcon}
        />
      ),
    },
  ];

  listActions = [
    {
      getTitle: () => "",
      renderAction: () => {
        const { classes } = this.props;

        return (
          <Link
            className={classes.link}
            onClick={this.redirectToSpecificationGapReport}
          >
            See full report
          </Link>
        );
      },
    },
  ];

  renderCompetencyStatusIcon = ({ status }) => (
    <CompetencyStatusIcon status={status} noBorder />
  );

  renderTabsIcon = () => {
    const { classes, competencyStatus } = this.props;

    return (
      <Grid item xs container className={classes.competencyStatusIconContainer}>
        <Grid container component={Divider} />
        <CompetencyStatusIcon
          status={competencyStatus}
          noBorder
          classes={{
            competencyStatusIcon: classes.competencyStatusIcon,
          }}
        />
      </Grid>
    );
  };

  renderTabs = ({ formValueSelector }) => (
    <CreateCardMoveTopContent
      renderIcon={this.renderTabsIcon}
      formValueSelector={formValueSelector}
    />
  );

  getKey = item => item.get("uniqueId");

  renderIcon = item =>
    ({
      [REQUIREMENT_TYPES.SKILL]: <ICONS.SKILL />,
      [REQUIREMENT_TYPES.COURSE]: <ICONS.COURSE />,
    }[item.get("type")]);

  render() {
    const {
      targetCardContainerId,
      missingCompetencyStatusItems,
      dispatchAllocateEmployeeOnSubmit,
    } = this.props;

    // check if card is being dragged or the competency status icon is clicked
    const cardAllocated = !!targetCardContainerId;

    const columns = this.createColumns(cardAllocated);

    return (
      <SimpleFormModal
        Icon={null}
        submitText="Allocate"
        renderTabs={this.renderTabs}
        displaySubmitBtn={cardAllocated}
        modalId={MISSING_COMPETENCE_MODAL_ID}
        onSubmit={dispatchAllocateEmployeeOnSubmit}
        title={cardAllocated ? "CRITICAL COMPETENCE MISSING" : "Competence"}
        DialogProps={{
          maxWidth: "md",
        }}
        form={{
          form: CREATE_CARD_MOVE_FORM.ID,
        }}
      >
        <SortableList
          columns={columns}
          stickyHeader={false}
          getKey={this.getKey}
          renderIcon={this.renderIcon}
          listActions={this.listActions}
          items={missingCompetencyStatusItems}
          noResultsMessage={
            cardAllocated
              ? "No missing critical courses or skills.."
              : "No courses or skills required.."
          }
          SortableListActionsHeaderProps={{
            transparent: true,
            displaySearchField: false,
          }}
        />
      </SimpleFormModal>
    );
  }
}

const styles = theme => ({
  link: {
    cursor: "pointer",
  },
  competencyStatusIcon: {
    top: "50%",
    left: "50%",
    position: "absolute",
    fontSize: theme.spacing(6),
    transform: "translate(-50%, -50%)",
  },
  competencyStatusIconContainer: {
    position: "relative",
  },
});

SpecificationGapReportModal.defaultProps = {
  missingCompetencyStatusItems: EMPTY_LIST,
};

export default compose(
  connect(
    state => {
      const selector = formValueSelector(CREATE_CARD_MOVE_FORM.ID);

      const cardId = selector(state, CREATE_CARD_MOVE_FORM.CARD);
      const targetGroupId = selector(
        state,
        CREATE_CARD_MOVE_FORM.TARGET_GROUP_ID,
      );

      return {
        cardId,
        groupId: targetGroupId,
        targetCardContainerId: selector(
          state,
          CREATE_CARD_MOVE_FORM.TARGET_CARD_CONTAINER_ID,
        ),
        missingCompetencyStatusItems:
          cardId && targetGroupId
            ? getCardMissingCompetencyStatusItemsFromState(
                state,
                targetGroupId,
                cardId,
              )
            : undefined,
        competencyStatus:
          cardId && targetGroupId
            ? getSummarizedCardCompetencyStatusFromState(
                state,
                targetGroupId,
                cardId,
              )
            : undefined,
      };
    },
    {
      dispatchAllocateEmployeeOnSubmit: allocateEmployeeOnSubmit,
    },
  ),
  withStyles(styles),
)(SpecificationGapReportModal);
